import React, { useEffect, useState } from "react";
import { Calendar, View, momentLocalizer } from "react-big-calendar";
import moment from "moment-timezone";
import intl from "react-intl-universal";
import { useHistory } from "react-router-dom";
import { useMutate } from "restful-react";
import { makeStyles } from "@material-ui/core/styles";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useSnackbar } from "notistack";

import { useQuery } from "hooks";
import { routing } from "routing";
import { searchMatch, ISearchMatchInput, IMatchesResponse } from "apis";
import { getMonthBoundaryInUtc } from "utils";

import { buildMatchTitle, getMatchEventColor } from "./utils";
import { IMatchEvent } from "./types";

const { Urls } = routing;

const localizer = momentLocalizer(moment);
const allViews: View[] = ["month", "day"];
const QUERY_KEY_DATE = "date";
const DATE_PATTERN = "YYYY-MM";

const useStyles = makeStyles(() => ({
  calendarHolder: {
    height: "700px",
  },
}));

export const ScheduleComponent = () => {
  const classes = useStyles();
  const query = useQuery();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { mutate } = useMutate({
    verb: "POST",
    path: searchMatch(),
  });
  const [matchEvents, setMatchEvents] = useState<IMatchEvent[]>([]);
  const queryParamsDate = query.get(QUERY_KEY_DATE);

  const currentMoment = moment(queryParamsDate, DATE_PATTERN, true);

  useEffect(() => {
    const searchMatchesInMonth = (currentMonth: string) => {
      if (!currentMonth) {
        return;
      }

      const range = getMonthBoundaryInUtc(currentMonth);
      const input: ISearchMatchInput = {
        matchStartFrom: range.from,
        matchEndTo: range.to,
      };
      mutate(input).then((response: IMatchesResponse) => {
        if (response.errCode > 0) {
          enqueueSnackbar(response.errMessage, {
            variant: "error",
          });
          return;
        }
        const events: IMatchEvent[] = response.matches.map((match) => ({
          id: match.id,
          title: buildMatchTitle(match),
          start: new Date(match.startTime),
          end: new Date(match.startTime),
          rawData: match,
        }));
        setMatchEvents(events);
      });
    };
    const unlistenCallback = history.listen((location) => {
      const currentMonth = new URLSearchParams(location.search).get(
        QUERY_KEY_DATE
      );
      searchMatchesInMonth(currentMonth as string);
    });
    searchMatchesInMonth(queryParamsDate as string);
    return () => {
      unlistenCallback();
    };
  }, [history, enqueueSnackbar, mutate, queryParamsDate]);

  const messages = {
    allDay: intl.get("SCHEDULE_CALENDAR_WHOLE_DAY"),
    previous: intl.get("SCHEDULE_CALENDAR_LAST_MONTH"),
    next: intl.get("SCHEDULE_CALENDAR_NEXT_MONTH"),
    today: intl.get("SCHEDULE_CALENDAR_TODAY"),
    month: intl.get("SCHEDULE_CALENDAR_MONTH"),
    week: intl.get("SCHEDULE_CALENDAR_WEEK"),
    day: intl.get("SCHEDULE_CALENDAR_DAY"),
    agenda: intl.get("SCHEDULE_CALENDAR_AGENDA"),
    showMore: () => intl.get("SCHEDULE_CALENDAR_MORE"),
  };

  const handleNavigate = (timeString: Date) => {
    const navigateMoment = moment(timeString);
    history.push(Urls.CALENDAR_WITH_DATE(navigateMoment.format(DATE_PATTERN)));
  };

  const eventPropHandler = (event: IMatchEvent) => {
    return { style: { background: getMatchEventColor(event.rawData) } };
  };

  const handleSelect = (data: IMatchEvent) => {
    history.push(Urls.MATCH_STATS(data.id));
  };

  return (
    <div className={classes.calendarHolder}>
      <Calendar
        events={matchEvents}
        views={allViews}
        step={60}
        showMultiDayTimes
        messages={messages}
        localizer={localizer}
        onNavigate={handleNavigate}
        date={currentMoment.toDate()}
        eventPropGetter={eventPropHandler}
        onSelectEvent={handleSelect}
      />
    </div>
  );
};
